import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, GlobalStyles } from '@mui/material';

/** Class SearchPointer puts on the control it points at. */
export const POINTER_CLASS = 'search-pointer-ring';

export interface SearchPointerState { label?: string; hint?: string; targets?: string[]; }

const LOOK_FOR_MS = 10000;
const RING_MS = 6000;
const TIP_MS = 12000;

/**
 * After the header search opens an in-page task ("Reset someone's password"), shows its tip on the
 * page and rings the control it names (by `data-help` key), waiting for the page to load it. On a
 * list every row's control is ringed, so nobody takes the first row's button for the one to click,
 * and the control's icon (the one most rows show) is copied into the tip. The pointer rides in the history state once and is
 * cleared, so a reload does not show it again.
 */
const SearchPointer: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [pointer, setPointer] = useState<SearchPointerState | null>(null);
    const [hasIcon, setHasIcon] = useState(false);
    const [missing, setMissing] = useState(false);
    const iconSlot = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const sent = (location.state as { searchPointer?: SearchPointerState } | null)?.searchPointer;
        if (!sent) return;
        setPointer(sent);
        const { searchPointer, ...rest } = location.state as Record<string, unknown>;
        navigate(location.pathname + location.search + location.hash,
            { replace: true, state: Object.keys(rest).length ? rest : null });
    }, [location, navigate]);

    useEffect(() => {
        if (!pointer) return;
        // Restarts when the control is found missing, so the note stays long enough to read.
        const tipTimer = window.setTimeout(() => setPointer(null), TIP_MS);
        return () => window.clearTimeout(tipTimer);
    }, [pointer, missing]);

    useEffect(() => {
        const targets = pointer?.targets ?? [];
        setHasIcon(false);
        setMissing(false);
        if (targets.length === 0) return;
        let ringed: Element[] = [];
        let ringTimer: number | undefined;
        const unring = () => ringed.forEach(el => el.classList.remove(POINTER_CLASS));
        const started = Date.now();
        const look = window.setInterval(() => {
            const found = targets.map(key => Array.from(document.querySelectorAll(`[data-help="${key}"]`)))
                .find(all => all.length > 0);
            if (found) {
                window.clearInterval(look);
                // One key can show different icons by row (a locked role shows an eye, not the pencil):
                // point only at the icon most rows show.
                // Compared by drawing: production MUI icons carry no data-testid.
                const iconOf = (el: Element) => el.querySelector('svg')?.innerHTML ?? '';
                const counts = new Map<string, number>();
                found.forEach(el => counts.set(iconOf(el), (counts.get(iconOf(el)) ?? 0) + 1));
                const common = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
                ringed = found.filter(el => iconOf(el) === common);
                ringed.forEach(el => el.classList.add(POINTER_CLASS));
                ringed[0].scrollIntoView?.({ block: 'center', behavior: 'smooth' });
                const icon = ringed[0].querySelector('svg');
                if (icon && iconSlot.current) {
                    const copy = icon.cloneNode(true) as SVGElement;
                    copy.style.color = window.getComputedStyle(icon).color;
                    copy.style.width = copy.style.height = '20px';
                    iconSlot.current.replaceChildren(copy);
                    setHasIcon(true);
                }
                ringTimer = window.setTimeout(unring, RING_MS);
            } else if (Date.now() - started > LOOK_FOR_MS) {
                window.clearInterval(look);
                setMissing(true);
            }
        }, 250);
        return () => {
            window.clearInterval(look);
            window.clearTimeout(ringTimer);
            unring();
        };
    }, [pointer]);

    return (
        <>
            <GlobalStyles styles={{
                '@keyframes searchPointerPulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(237,108,2,0.7)' },
                    '70%': { boxShadow: '0 0 0 12px rgba(237,108,2,0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(237,108,2,0)' },
                },
                [`.${POINTER_CLASS}`]: {
                    outline: '2px solid #ed6c02', outlineOffset: 2, borderRadius: 6,
                    animation: 'searchPointerPulse 1.2s ease-out infinite',
                },
            }} />
            {pointer?.hint && (
                <Alert
                    severity="info"
                    icon={<span aria-hidden>👉</span>}
                    onClose={() => setPointer(null)}
                    sx={{ position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)', zIndex: 1400,
                        maxWidth: 'calc(100% - 32px)', width: 560, boxShadow: 3 }}
                >
                    {pointer.hint}
                    {missing && ' It is not on this screen: it may be inside a record, a tab or a dialog, or need access you do not have.'}
                    <Box component="span" sx={{ display: hasIcon ? 'inline-flex' : 'none', alignItems: 'center', gap: 0.75, ml: 1, fontWeight: 600 }}>
                        Look for:
                        <Box component="span" ref={iconSlot} sx={{ display: 'inline-flex', p: 0.25, border: '2px solid #ed6c02', borderRadius: 1, bgcolor: 'background.paper' }} />
                    </Box>
                </Alert>
            )}
        </>
    );
};

export default SearchPointer;
